import type { ComponentIR } from "../ir.js";
import type { GeneratedFile, GenerateOptions, Generator } from "./types.js";

/**
 * Emit a generated module exposing the resolved binding configs.
 *
 * The app's hand-written CLI handler imports this to obtain each binding's
 * `config` payload (and package name) at runtime, then delegates to the
 * binding package's `sync` / `check` function. Mirrors how the embedded DSL is
 * surfaced to the runtime.
 *
 * Emits nothing when the component declares no bindings — keeping output
 * byte-identical for components that don't use bindings (backward compat).
 */
export const bindingsGenerator: Generator = {
  generate(ir: ComponentIR, _options: GenerateOptions): GeneratedFile[] {
    if (!ir.bindings || Object.keys(ir.bindings).length === 0) {
      return [];
    }

    const entries = Object.entries(ir.bindings).map(([id, binding]) => {
      return `  ${JSON.stringify(id)}: {\n    package: ${JSON.stringify(binding.package)},\n    command: ${JSON.stringify(binding.command)},\n    config: ${JSON.stringify(binding.config)},\n  },`;
    });

    const content = `// Auto-generated by @aaac/contracts. Do not edit.

export interface BindingEntry {
  /** npm package implementing the binding. */
  package: string;
  /** CLI command name exposed for this binding. */
  command: string;
  /** Binding-specific configuration (validated by the binding package). */
  config: Record<string, unknown>;
}

export const bindings: Record<string, BindingEntry> = {
${entries.join("\n")}
};
`;

    return [
      {
        path: "bindings.generated.ts",
        content,
        overwrite: true,
      },
    ];
  },
};
